import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Download, FileText, Link2, Loader2, Trash2, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/data-table/DataTable';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useOperations } from '@/hooks/queries/useOperations';
import {
  getSignedUrl,
  useBulkDeleteJustificatifs,
  useDeleteJustificatif,
  useJustificatifs,
  useUploadJustificatif,
} from '@/hooks/queries/useJustificatifs';
import { supabase } from '@/lib/supabase';
import { useRole } from '@/hooks/useRole';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';
import { formatDateTimeDZ, formatNumberDZ } from '@/lib/format';
import { t } from '@/locales/i18n';
import type { JustificatifRow } from '@/lib/database.types';

const RELATED_LABELS: Record<string, string> = {
  operation: 'Opération',
  facture: 'Facture',
  echeance: 'Échéance',
  cloture: 'Clôture',
  rapprochement: 'Rapprochement',
  note_frais: 'Note de frais',
};

export function JustificatifsPage(): JSX.Element {
  const { isAdmin } = useRole();
  const canView = usePermission('justificatifs.view');
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  const [search, setSearch] = useState('');
  const [relatedType, setRelatedType] = useState<string>('');
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: rows = [], isLoading } = useJustificatifs({
    entrepriseId,
    relatedType: relatedType || null,
    search,
  });
  const upload = useUploadJustificatif();
  const remove = useDeleteJustificatif();
  const bulkDelete = useBulkDeleteJustificatifs();

  const [confirmDelete, setConfirmDelete] = useState<JustificatifRow | null>(null);
  const [selectedRows, setSelectedRows] = useState<JustificatifRow[]>([]);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const [bulkLinkOpen, setBulkLinkOpen] = useState(false);
  const clearSelection = (): void => setSelectionVersion((v) => v + 1);

  const onFile = async (file: File): Promise<void> => {
    if (!entrepriseId) {
      toast.error('Sélectionnez une entreprise');
      return;
    }
    try {
      const j = await upload.mutateAsync({ entreprise_id: entrepriseId, file });
      if ((j as JustificatifRow).id) toast.success('Justificatif uploadé');
    } catch (e) {
      toast.error('Erreur : ' + (e as Error).message);
    }
  };

  const open = async (j: JustificatifRow): Promise<void> => {
    const url = await getSignedUrl(j.file_path, 600);
    if (url) window.open(url, '_blank');
    else toast.error('Lien indisponible');
  };

  const columns = useMemo<ColumnDef<JustificatifRow>[]>(
    () => [
      {
        accessorKey: 'file_name',
        header: 'Fichier',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="truncate font-medium">{row.original.file_name}</span>
          </div>
        ),
      },
      {
        accessorKey: 'mime_type',
        header: 'Type',
        size: 100,
        cell: ({ getValue }) => (
          <Badge variant="outline">{String(getValue()).split('/')[1] ?? String(getValue())}</Badge>
        ),
      },
      {
        accessorKey: 'related_type',
        header: 'Lié à',
        size: 130,
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? (
            <Badge variant="secondary">{RELATED_LABELS[v] ?? v}</Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      },
      {
        accessorKey: 'size_bytes',
        header: 'Taille (kB)',
        size: 100,
        cell: ({ getValue }) => (
          <span className="num text-xs">
            {formatNumberDZ(Math.round(Number(getValue()) / 1024))}
          </span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Uploadé le',
        size: 150,
        cell: ({ getValue }) => (
          <span className="text-xs">{formatDateTimeDZ(String(getValue()))}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 80,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => void open(row.original)}
              aria-label="Ouvrir"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
            {isAdmin && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={() => setConfirmDelete(row.original)}
                aria-label="Supprimer"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [isAdmin],
  );

  if (!canView) return <AccessDenied feature="justificatifs.view" />;

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={t('finances.justificatifs.title')}
        subtitle={t('finances.justificatifs.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md bg-primary px-3 text-sm text-primary-foreground hover:bg-primary/90">
              {upload.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {t('finances.justificatifs.upload')}
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                disabled={upload.isPending}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFile(f);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
        }
      />

      <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-card p-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Type lié</label>
          <select
            value={relatedType}
            onChange={(e) => setRelatedType(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">— Tous —</option>
            {Object.entries(RELATED_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto space-y-1">
          <label className="text-xs text-muted-foreground">Recherche fichier</label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom du fichier…"
            className="h-9 w-64"
          />
        </div>
      </div>

      <DataTable
        key={selectionVersion}
        data={rows}
        columns={columns}
        loading={isLoading}
        pageSize={20}
        searchPlaceholder="Rechercher un justificatif…"
        exportFileName="justificatifs.csv"
        enableSelection={isAdmin}
        getRowId={(row) => row.id}
        onSelectionChange={setSelectedRows}
        emptyMessage="Aucun justificatif."
        bulkBar={
          <BulkActionBar count={selectedRows.length} onClear={clearSelection}>
            <BulkButton onClick={() => exportJustifsCSV(selectedRows)}>
              <Download className="h-3.5 w-3.5" /> Exporter CSV
            </BulkButton>
            <BulkButton onClick={() => setBulkLinkOpen(true)}>
              <Link2 className="h-3.5 w-3.5" /> Lier à une opération
            </BulkButton>
            {isAdmin && (
              <BulkButton variant="destructive" onClick={() => setBulkConfirmDelete(true)}>
                <Trash2 className="h-3.5 w-3.5" /> Supprimer
              </BulkButton>
            )}
          </BulkActionBar>
        }
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer « ${confirmDelete?.file_name} » ?`}
        description="Le fichier sera retiré du Storage et de la base."
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await remove.mutateAsync(confirmDelete);
          toast.success('Justificatif supprimé');
          setConfirmDelete(null);
        }}
      />

      <DangerousConfirmDialog
        open={bulkConfirmDelete}
        onOpenChange={setBulkConfirmDelete}
        title={`Supprimer ${selectedRows.length} justificatif${selectedRows.length > 1 ? 's' : ''} ?`}
        description="Action irréversible. Les fichiers Storage et leurs références seront supprimés."
        consequences={[
          `${selectedRows.length} fichier(s) seront retirés du bucket "documents"`,
          'Les références côté opérations passeront à NULL',
          "L'action est journalisée dans audit_log",
        ]}
        challenge="SUPPRIMER"
        loading={bulkDelete.isPending}
        onConfirm={async () => {
          const r = await bulkDelete.mutateAsync(selectedRows);
          if (r.errors.length === 0)
            toast.success(`${r.success.length} justificatif(s) supprimé(s)`);
          else toast.warning(`${r.success.length} OK · ${r.errors.length} échec(s)`);
          setBulkConfirmDelete(false);
          clearSelection();
        }}
      />

      <BulkLinkDialog
        open={bulkLinkOpen}
        onOpenChange={setBulkLinkOpen}
        entrepriseId={entrepriseId}
        justificatifs={selectedRows}
        onLinked={() => {
          clearSelection();
          setBulkLinkOpen(false);
        }}
      />
    </div>
  );
}

function exportJustifsCSV(rows: JustificatifRow[]): void {
  if (rows.length === 0) return;
  const headers = [
    'file_name',
    'mime_type',
    'size_bytes',
    'related_type',
    'related_id',
    'sha256',
    'created_at',
  ];
  const csv = [
    headers.join(','),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const v = (r as unknown as Record<string, unknown>)[h];
          const s = v == null ? '' : String(v);
          return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `justificatifs-${rows.length}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`${rows.length} ligne(s) exportée(s)`);
}

function BulkLinkDialog({
  open,
  onOpenChange,
  entrepriseId,
  justificatifs,
  onLinked,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
  justificatifs: JustificatifRow[];
  onLinked: () => void;
}): JSX.Element {
  const [opSearch, setOpSearch] = useState('');
  const { data: opsData } = useOperations({
    entrepriseId,
    search: opSearch,
    pageSize: 50,
  });
  const ops = opsData?.rows ?? [];
  const [selectedOpId, setSelectedOpId] = useState('');
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedOpId('');
      setOpSearch('');
    }
  }, [open]);

  const link = async (): Promise<void> => {
    if (!selectedOpId || justificatifs.length === 0) return;
    setLinking(true);
    try {
      // 1. Met à jour les justificatifs (related_type/id)
      const { error } = await supabase
        .from('justificatifs')
        .update({ related_type: 'operation', related_id: selectedOpId })
        .in(
          'id',
          justificatifs.map((j) => j.id),
        );
      if (error) throw error;
      // 2. Met à jour l'opération avec le PREMIER justificatif (champ unique côté op)
      await supabase
        .from('operations')
        .update({ justificatif_id: justificatifs[0]!.id })
        .eq('id', selectedOpId);
      toast.success(`${justificatifs.length} justificatif(s) lié(s) à l'opération`);
      onLinked();
    } catch (e) {
      toast.error('Erreur : ' + (e as Error).message);
    } finally {
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Lier {justificatifs.length} justificatif{justificatifs.length > 1 ? 's' : ''} à une
            opération
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Sélectionnez l&apos;opération cible. Les justificatifs garderont leur référence
            polymorphique et l&apos;opération pointera sur le premier d&apos;entre eux.
          </p>
          <Input
            value={opSearch}
            onChange={(e) => setOpSearch(e.target.value)}
            placeholder="Rechercher (réf, motif…)"
            className="h-9"
          />
          <div className="scrollbar-thin max-h-72 overflow-y-auto rounded-md border border-border">
            {ops.length === 0 ? (
              <p className="p-3 text-xs text-muted-foreground">Aucune opération trouvée.</p>
            ) : (
              ops.map((op) => (
                <label
                  key={op.id}
                  className={
                    'flex cursor-pointer items-center gap-2 border-b border-border px-3 py-2 text-sm last:border-0 hover:bg-muted/40 ' +
                    (selectedOpId === op.id ? 'bg-primary/10' : '')
                  }
                >
                  <input
                    type="radio"
                    name="op"
                    checked={selectedOpId === op.id}
                    onChange={() => setSelectedOpId(op.id)}
                    className="accent-primary"
                  />
                  <span className="font-mono text-xs">{op.reference}</span>
                  <span className="flex-1 truncate">{op.motif}</span>
                  <Badge variant="outline">{op.statut}</Badge>
                </label>
              ))
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={linking}>
            Annuler
          </Button>
          <Button disabled={!selectedOpId || linking} onClick={() => void link()}>
            {linking && <Loader2 className="h-4 w-4 animate-spin" />}
            Lier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
